import { COMMENT_LINK_PROTOCOLS, COMMENT_TAGS } from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ux-pass-2 · rich text out of an audit row, rendered — and sanitised HERE. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A comment body, an invoice note and a description travel through the history as
 * `{ kind: 'html' }`. Every other screen that renders one trusts the column it read, because the
 * service sanitised it on the way in (`task-detail.tsx`, `invoice-detail.tsx`). This one cannot
 * lean on that as hard: an audit row is a COPY taken at write time, from whichever version of
 * whichever writer produced it, and a `before` may predate the sanitiser that guards the live
 * column today.
 *
 * ⚠️⚠️ **SO THE SAME ALLOW-LIST, APPLIED A SECOND TIME, IN THE BROWSER.** `COMMENT_TAGS` and
 * `COMMENT_LINK_PROTOCOLS` are the server's own lists (`comment-markup.ts` — *one list, both
 * sides*). An element outside the list is unwrapped and keeps its words; a script, a style or an
 * embedded document is dropped with its content; every attribute but a safe `href` is removed.
 *
 * ⚠️ **A `<template>`, never a live element.** Parsing into a template's content does not run
 * scripts or fetch images, so the markup is inert from the moment it is parsed to the moment it is
 * serialised back out.
 */

const ALLOWED: ReadonlySet<string> = new Set(COMMENT_TAGS.filter((tag) => tag !== '#text'));

/** Dropped WITH their content — their text is code or a document, never prose. */
const DROPPED: ReadonlySet<string> = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'template',
  'noscript',
  'svg',
  'math',
  'title',
  'textarea',
]);

const PROTOCOLS: ReadonlySet<string> = new Set(COMMENT_LINK_PROTOCOLS.map((one) => `${one}:`));

function safeHref(value: string): boolean {
  try {
    /* ⚠️ Resolved against a placeholder base, so a relative link passes as `https:` and
       `javascript:` — in any case, with any whitespace the parser forgives — is caught by name. */
    return PROTOCOLS.has(new URL(value, 'https://placeholder.invalid').protocol);
  } catch {
    return false;
  }
}

function clean(parent: ParentNode): void {
  for (const child of [...parent.childNodes]) {
    if (child.nodeType === Node.TEXT_NODE) continue;
    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.remove();
      continue;
    }
    const element = child as Element;
    const tag = element.tagName.toLowerCase();
    if (DROPPED.has(tag)) {
      element.remove();
      continue;
    }
    clean(element);
    if (!ALLOWED.has(tag)) {
      element.replaceWith(...element.childNodes);
      continue;
    }
    for (const attribute of [...element.attributes]) {
      const keep = tag === 'a' && attribute.name === 'href' && safeHref(attribute.value);
      if (!keep) element.removeAttribute(attribute.name);
    }
    if (tag === 'a') {
      /* The same two the server writes: a link opened in this tab would hand the destination a
         `window.opener` handle on the app. */
      element.setAttribute('rel', 'noopener noreferrer nofollow');
      element.setAttribute('target', '_blank');
    }
  }
}

/** The allow-listed markup, ready for `dangerouslySetInnerHTML`. */
export function sanitiseHtml(html: string): string {
  const template = document.createElement('template');
  template.innerHTML = html;
  clean(template.content);
  return template.innerHTML;
}

/**
 * The words of a rich-text value, for a one-line summary.
 *
 * ⚠️ A space goes in at every block boundary first — `textContent` alone joins
 * `<p>Kész</p><p>Mehet</p>` into *KészMehet*.
 *
 * ⚠️ **Cleaned first, by the same walk.** `textContent` reads a `<script>`'s and a `<style>`'s
 * text too, so without it a summary would quote the code the sanitiser exists to drop.
 */
export function htmlToText(html: string): string {
  const template = document.createElement('template');
  template.innerHTML = html.replace(
    /<(?:br|\/p|\/li|\/h2|\/h3|\/blockquote|\/pre)\b[^>]*>/giu,
    ' $&',
  );
  clean(template.content);
  return template.content.textContent.replace(/\s+/gu, ' ').trim();
}
